import { useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import type { OrderListItem, OrderStatus } from '@/api/orders'
import { ordersApi } from '@/api/orders'
import { AppShell } from '@/components/AppShell'
import { IconClipboard, IconClose, IconSearch } from '@/components/icons'
import { Alert, Button, EmptyState, SkeletonList } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { StatusBadge } from '@/features/orders/StatusBadge'
import { dayKey, formatDate, formatRelativeDay } from '@/lib/format'
import { useAuthStore } from '@/stores/authStore'

type Filter = OrderStatus | 'all' | 'open' | 'awaiting'

const BUSINESS_FILTERS: Filter[] = ['all', 'open', 'sent', 'received', 'confirmed', 'delivered']
// Ο προμηθευτής σκέφτεται «τι μου ζητάνε απάντηση», όχι «τι έστειλα».
const SUPPLIER_FILTERS: Filter[] = ['all', 'awaiting', 'confirmed', 'partially_confirmed', 'delivered']
const STATUSES = new Set<string>(['sent', 'received', 'confirmed', 'partially_confirmed', 'delivered'])
const META_FILTERS = new Set<string>(['all', 'open', 'awaiting'])

/**
 * Λίστα παραγγελιών.
 *
 * Τα φίλτρα ζουν στη διεύθυνση (`?status=sent`, `?open=1`, `?deliveryDate=…`)
 * ώστε οι μετρητές της Αρχικής να οδηγούν κατευθείαν εδώ, και το «πίσω» του
 * browser να επιστρέφει στην ίδια όψη.
 */
export default function OrdersPage() {
  const { t } = useTranslation()
  const translateError = useApiError()
  const [params, setParams] = useSearchParams()
  const isSupplier = useAuthStore((s) => s.organizationType()) === 'supplier'
  const filters = isSupplier ? SUPPLIER_FILTERS : BUSINESS_FILTERS

  const statusParam = params.get('status')
  const filter: Filter =
    params.get('open') === '1'
      ? 'open'
      : params.get('awaiting') === '1'
        ? 'awaiting'
        : STATUSES.has(statusParam ?? '')
          ? (statusParam as OrderStatus)
          : 'all'
  const deliveryDate = params.get('deliveryDate')

  // Η αναζήτηση γράφεται άμεσα αλλά ρωτά τον server με μικρή καθυστέρηση:
  // ένα αίτημα ανά γράμμα θα έκανε τη λίστα να τρεμοπαίζει.
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const setFilter = (next: Filter) => {
    const nextParams = new URLSearchParams(params)
    nextParams.delete('status')
    nextParams.delete('open')
    nextParams.delete('awaiting')
    if (next === 'open') nextParams.set('open', '1')
    else if (next === 'awaiting') nextParams.set('awaiting', '1')
    else if (next !== 'all') nextParams.set('status', next)
    setParams(nextParams, { replace: true })
  }

  const clearDeliveryDate = () => {
    const nextParams = new URLSearchParams(params)
    nextParams.delete('deliveryDate')
    setParams(nextParams, { replace: true })
  }

  const orders = useInfiniteQuery({
    queryKey: ['orders', filter, deliveryDate, debounced],
    queryFn: ({ pageParam }) =>
      ordersApi.list({
        status: STATUSES.has(filter) ? (filter as OrderStatus) : undefined,
        open: filter === 'open',
        awaiting: filter === 'awaiting',
        deliveryDate: deliveryDate ?? undefined,
        q: debounced || undefined,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.pageSize < lastPage.total ? lastPage.page + 1 : undefined,
  })

  const items = useMemo(() => orders.data?.pages.flatMap((page) => page.items) ?? [], [orders.data])
  const total = orders.data?.pages[0]?.total ?? 0

  // Ομαδοποίηση ανά ημέρα δημιουργίας. Ο χρήστης ψάχνει «αυτό που έστειλα
  // χθες», όχι έναν αριθμό.
  const groups = useMemo(() => {
    const byDay = new Map<string, { label: string; orders: OrderListItem[] }>()
    for (const order of items) {
      const key = dayKey(order.createdAt)
      const group = byDay.get(key) ?? { label: formatRelativeDay(order.createdAt, t), orders: [] }
      group.orders.push(order)
      byDay.set(key, group)
    }
    return [...byDay.values()]
  }, [items, t])

  return (
    <AppShell title={t(isSupplier ? 'nav.incoming' : 'nav.orders')}>
      <label className="relative mb-3 block">
        <IconSearch size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t(isSupplier ? 'orders.searchPlaceholderSupplier' : 'orders.searchPlaceholder')}
          aria-label={t('common.search')}
          className="h-touch w-full rounded-xl border border-gray-300 bg-white pl-10 pr-4 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
        />
      </label>

      <div className="mb-3 -mx-4 overflow-x-auto px-4">
        <div className="flex w-max gap-2">
          {filters.map((value) => (
            <button
              key={value}
              onClick={() => setFilter(value)}
              aria-pressed={filter === value}
              className={
                'rounded-full border px-4 py-2 text-sm font-medium transition-colors ' +
                (filter === value
                  ? 'border-brand-700 bg-brand-700 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400')
              }
            >
              {META_FILTERS.has(value)
                ? t(`orders.filters.${value}`)
                : t(`orders.status.${value}`)}
            </button>
          ))}
        </div>
      </div>

      {deliveryDate && (
        <div className="mb-3">
          <button
            onClick={clearDeliveryDate}
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-accent-200 bg-accent-50 pl-3 pr-2 text-sm font-medium text-accent-700"
          >
            {t('orders.deliveryOn', { date: formatRelativeDay(deliveryDate, t) })}
            <IconClose size={15} aria-label={t('orders.clearFilter')} />
          </button>
        </div>
      )}

      {orders.isPending && <SkeletonList rows={4} />}
      {orders.isError && <Alert>{translateError(orders.error)}</Alert>}

      {orders.data && items.length === 0 && (
        <EmptyState
          icon={<IconClipboard size={26} />}
          title={filter === 'all' && !deliveryDate && !debounced ? t('orders.empty') : t('orders.noneWithFilter')}
          description={
            filter === 'all' && !deliveryDate && !debounced
              ? t('orders.emptyHint')
              : t('orders.noneWithFilterHint')
          }
        />
      )}

      {groups.map((group) => (
        <section key={group.label} className="mb-4">
          <h2 className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-500">
            {group.label}
          </h2>
          <ul className="flex flex-col gap-2">
            {group.orders.map((order) => (
              <li key={order.id}>
                <Link
                  to={`/orders/${order.id}`}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-4 hover:border-brand-300"
                >
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-gray-900">
                      {isSupplier ? order.businessName : order.supplierName}
                    </p>
                    <p className="font-mono text-xs text-gray-500">{order.orderNumber}</p>
                    <p className="mt-1 text-xs text-gray-400">
                      {t('orders.itemCount', { count: order.itemCount })}
                      {order.requestedDeliveryDate &&
                        ` · ${t('orders.delivery')} ${formatDate(order.requestedDeliveryDate)}`}
                    </p>
                  </div>
                  <StatusBadge status={order.status} />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {orders.hasNextPage && (
        <div className="mt-2 flex flex-col items-center gap-2">
          <p className="text-xs text-gray-400">
            {t('orders.shown', { shown: items.length, total })}
          </p>
          <Button
            variant="secondary"
            loading={orders.isFetchingNextPage}
            onClick={() => void orders.fetchNextPage()}
          >
            {t('orders.loadMore')}
          </Button>
        </div>
      )}
    </AppShell>
  )
}
